import { _convertProductListToGA4 } from './helpers'
import { getStoreIsoCurrencySymbol } from '~/design-system/helpers/misc'
import { swiftCustomEventName } from '~/helpers/events'
import { formatPrice } from '~/helpers/number'
import type { EcommerceEvents } from '~/hooks/useTrackEvents'
import type { Cart, Product } from '~/type/Cart'

export const TrackGtmEvents = (
  push: (args: Record<string, any>) => void,
  options: {
    variableName: string
  },
) => {
  const { variableName } = options
  const windowObject = ((window as any) || {})[variableName]

  /**
   * Reset dataLayer Enhanced Ecommerce
   * https://developers.google.com/tag-manager/enhanced-ecommerce#clear-ecommerce
   */
  const clearEE = () => {
    push({
      ecommerce: null,
      gaIsNotUserInteraction: true,
    })
  }

  const subscribe = (
    event: keyof typeof EcommerceEvents,

    data: {
      data: {
        event: string
        products?: Product[]
        list?: string
        shippingTier?: string
        step?: string
        paymentType?: string
        ecommerData?: any
        cart?: Cart
        /**
         {
          transaction_id: string
          affiliation: string
          value: string
          currency: string
          tax: string
          shipping: string
          coupon: string
        }
         */
      }
    },
  ) => {
    const products = data?.data.products?.map((product) => ({
      ...product,
      price: formatPrice(product.price ?? 0),
      listPrice: formatPrice(product.listPrice ?? 0),
    }))

    const list = data?.data.list

    switch (true) {
      case event == 'beginCheckout': {
        if (windowObject?.find((item: any) => item.event === 'begin_checkout')) {
          return
        }

        clearEE()
        push({
          event: 'begin_checkout',
          gaIsNotUserInteraction: true,
          ecommerce: {
            items: _convertProductListToGA4(products),
          },
        })
        break
      }

      case event == 'viewCart': {
        if (windowObject?.find((item: any) => item.event === 'view_cart')) {
          return
        }

        clearEE()
        push({
          event: 'view_cart',
          gaIsNotUserInteraction: true,
          ecommerce: {
            currency: getStoreIsoCurrencySymbol(),
            items: _convertProductListToGA4(products),
            value: formatPrice(data.data.cart?.totalizers.reduce((acc, att) => acc + att.value, 0) ?? 0),
          },
        })
        break
      }

      case event === 'addToCart': {
        clearEE()
        push({
          event: 'addToCart',
          ecommerce: {
            currencyCode: getStoreIsoCurrencySymbol(),
            add: {
              actionField: { list, action: 'add' },
              products,
            },
          },
        })
        clearEE()
        push({
          event: 'add_to_cart',
          ecommerce: {
            items: _convertProductListToGA4(products),
          },
        })
        break
      }

      case event == 'removeFromCart': {
        clearEE()
        push({
          event: 'removeFromCart',
          ecommerce: {
            currencyCode: getStoreIsoCurrencySymbol(),
            remove: {
              actionField: { list },
              products,
            },
          },
        })

        clearEE()
        push({
          event: 'remove_from_cart',
          gaIsNotUserInteraction: true,
          ecommerce: {
            currencyCode: getStoreIsoCurrencySymbol(),
            items: _convertProductListToGA4(products),
          },
          ecommerceV2: {
            currencyCode: getStoreIsoCurrencySymbol(),
            items: _convertProductListToGA4(products),
          },
        })
        break
      }

      case event === 'addShippingInfo': {
        const shippingTier = data?.data.shippingTier
        // one shot
        if (windowObject?.find((item: any) => item.event === 'add_shipping_info')) {
          return
        }

        clearEE()
        push({
          event: 'add_shipping_info',
          gaIsNotUserInteraction: true,
          ecommerce: {
            shipping_tier: shippingTier,
            items: _convertProductListToGA4(products),
          },
        })

        break
      }

      case event === 'viewStepCheckout': {
        const step = data?.data.step
        clearEE()
        push({
          event: 'checkout',
          gaIsNotUserInteraction: true,
          ecommerce: {
            checkout: {
              actionField: { step },
              products,
            },
          },
        })
        break
      }

      case event == 'addPaymentInfo': {
        const paymentType = data?.data.paymentType
        clearEE()
        const payload = {
          event: 'add_payment_info',
          gaIsNotUserInteraction: true,
          ecommerce: {
            payment_type: paymentType,
            items: _convertProductListToGA4(products),
          },
        } as any

        push(payload)
        break
      }

      case event == 'orderPlaced': {
        const purchaseData = {
          ecommerce: {
            currency: getStoreIsoCurrencySymbol(),
            transaction_id: data.data.ecommerData.orderId,
            value: formatPrice(data.data.ecommerData.value ?? 0),
            coupon: null,
            shipping: formatPrice(
              data.data.ecommerData.totals.find((total: any) => total.id === 'Shipping')?.value ?? 0,
            ),
            tax: 0,
            items: data.data.ecommerData.items.map((item: any) => ({
              ...item,
              price: formatPrice(item.price ?? 0),
            })),
          },
        }

        if (
          windowObject?.some(
            (item: any) =>
              String(item?.ecommerce?.transaction_id) === String(purchaseData.ecommerce.transaction_id),
          )
        ) {
          return
        }

        clearEE()
        push({
          // TODO: review this attribute
          ecommerce: {
            ...data?.data.ecommerData,
            items: data?.data.ecommerData.items.map((item: any) => ({
              ...item,
              price: formatPrice(item.price ?? 0),
            })),
            value: formatPrice(data.data.ecommerData.value ?? 0),
          },
          event: 'orderPlaced',
          gaIsNotUserInteraction: true,
        })
        clearEE()

        push({
          event: 'purchase',
          gaIsNotUserInteraction: true,
          ecommerce: {
            ...purchaseData.ecommerce,
          },
        })

        break
      }
    }
  }

  document.addEventListener(swiftCustomEventName, (e: any) => {
    subscribe(e.detail.name, e.detail)
  })
}
